import {
  CHECKS_24H_DISPLAY_FLOOR,
  SEAL_STATUS_AWAITING,
  SEAL_STATUS_ENFORCED,
  SEAL_STATUS_ENFORCED_PLAIN,
  plainStatusWithChecks,
  sealStatusWithChecks,
} from './trustCopy';
import type { PublicCount, PublicTrustSnapshot, TrustSealState } from './types';

/** Any checks at all. Null is 1 to 24 checks (below the display floor). */
function hasChecks(count: PublicCount | undefined): boolean {
  return count === null || (count ?? 0) > 0;
}

/** The number that may be printed: a null (below the floor) never reaches it. */
function shownCount(count: PublicCount | undefined): number {
  return count ?? 0;
}

/** Status semantics aligned with badge_svg.py. */
export function trustSealState(snapshot: PublicTrustSnapshot): TrustSealState {
  if (hasChecks(snapshot.checks_24h)) {
    return 'active';
  }
  if (snapshot.enforcement_active) {
    return 'recent';
  }
  return 'idle';
}

/**
 * EU check counts behind the seal label. The seal is region agnostic: the law
 * it names follows the jurisdiction the checks ran under, never the data region.
 * A payload without eu_checks_* predates that rule (API not yet redeployed), so
 * it is read the way that API labelled it: every check counted as EU.
 */
function euCheckCounts(snapshot: PublicTrustSnapshot): {
  eu24h: number;
  eu30d: number;
  namesEuAiAct: boolean;
} {
  const legacy = snapshot.eu_checks_24h === undefined && snapshot.eu_checks_30d === undefined;
  if (legacy) {
    return {
      eu24h: shownCount(snapshot.checks_24h),
      eu30d: shownCount(snapshot.checks_30d),
      namesEuAiAct: hasChecks(snapshot.checks_24h) || snapshot.enforcement_active,
    };
  }
  return {
    eu24h: shownCount(snapshot.eu_checks_24h),
    eu30d: shownCount(snapshot.eu_checks_30d),
    namesEuAiAct: hasChecks(snapshot.eu_checks_24h) || hasChecks(snapshot.eu_checks_30d),
  };
}

/** True when the seal names the EU AI Act: at least one EU check in 30 days. */
export function sealNamesEuAiAct(snapshot: PublicTrustSnapshot): boolean {
  return euCheckCounts(snapshot).namesEuAiAct;
}

/**
 * Seal right-panel label: mirrors badge_svg.badge_status_label exactly.
 * Counts beside "EU AI Act" are EU checks only; with no EU check the law is
 * not named. Counts below CHECKS_24H_DISPLAY_FLOOR are never shown.
 */
export function sealStatusLabel(snapshot: PublicTrustSnapshot): string {
  const floor = CHECKS_24H_DISPLAY_FLOOR;
  const checks24h = shownCount(snapshot.checks_24h);
  const checks30d = shownCount(snapshot.checks_30d);
  const { eu24h, eu30d, namesEuAiAct } = euCheckCounts(snapshot);
  if (eu24h >= floor) {
    return sealStatusWithChecks(eu24h);
  }
  if (eu30d >= floor) {
    return `${sealStatusWithChecks(eu30d)} (30d)`;
  }
  if (namesEuAiAct) {
    return SEAL_STATUS_ENFORCED;
  }
  if (checks24h >= floor) {
    return plainStatusWithChecks(checks24h);
  }
  if (checks30d >= floor) {
    return `${plainStatusWithChecks(checks30d)} (30d)`;
  }
  if (hasChecks(snapshot.checks_24h) || snapshot.enforcement_active) {
    return SEAL_STATUS_ENFORCED_PLAIN;
  }
  return SEAL_STATUS_AWAITING;
}

/**
 * Compact status line: parity with the seal label (same EU rule), worded for
 * a sentence. Raw counts below CHECKS_24H_DISPLAY_FLOOR are omitted.
 */
export function trustStatusHeadline(snapshot: PublicTrustSnapshot): string {
  const state = trustSealState(snapshot);
  const { eu24h, namesEuAiAct } = euCheckCounts(snapshot);
  const enforced = namesEuAiAct ? SEAL_STATUS_ENFORCED : SEAL_STATUS_ENFORCED_PLAIN;
  if (state === 'active') {
    if (eu24h >= CHECKS_24H_DISPLAY_FLOOR) {
      return `${sealStatusWithChecks(eu24h)} in 24h`;
    }
    const checks24h = shownCount(snapshot.checks_24h);
    if (!namesEuAiAct && checks24h >= CHECKS_24H_DISPLAY_FLOOR) {
      return `${plainStatusWithChecks(checks24h)} in 24h`;
    }
    return enforced;
  }
  if (state === 'recent') {
    return `${enforced} · quiet last 24h`;
  }
  return SEAL_STATUS_AWAITING;
}

/** Right-panel label for the trust-page seal: mirrors badge_svg.badge_status_label. */
export function trustHeroSealLabel(snapshot: PublicTrustSnapshot): string {
  return sealStatusLabel(snapshot);
}

export const TRUST_STATUS_STYLES: Record<
  TrustSealState,
  { banner: string; dot: string; label: string }
> = {
  active: {
    banner: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300',
    dot: 'bg-emerald-500',
    label: 'Live enforcement',
  },
  recent: {
    banner: 'bg-amber-500/15 border-amber-500/40 text-amber-200',
    dot: 'bg-amber-500',
    label: 'Enforcement active (30d)',
  },
  idle: {
    banner: 'bg-red-500/15 border-red-500/40 text-red-300',
    dot: 'bg-red-500',
    label: 'Awaiting activity',
  },
};

/** Display hostname for tenant origin link (PublicTrustSnapshot.website_url). */
export function trustSiteHostname(websiteUrl: string): string {
  try {
    return new URL(websiteUrl).hostname.replace(/^www\./, '');
  } catch {
    return websiteUrl;
  }
}

/** http(s) only; never javascript:/data: as an href. */
export function safeHttpUrl(url: string | null | undefined): string | undefined {
  if (!url || typeof url !== 'string') {
    return undefined;
  }
  try {
    const parsed = new URL(url.trim());
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
      return undefined;
    }
    if (parsed.username || parsed.password) {
      return undefined;
    }
    return parsed.href;
  } catch {
    return undefined;
  }
}
